// https://moment.github.io/luxon/#/formatting?id=table-of-tokens
import { DateTime, Duration } from "luxon";

export const isoToDate = (dateStr = "") => {
  return DateTime.fromISO(dateStr, { zone: "utc" });
};

export const prettyDate = (dateStr = "") => {
  return isoToDate(dateStr).toFormat("yyyy-MM-dd");
};

export const prettyTime = (dateStr = "") => {
  return isoToDate(dateStr).toFormat("HH:mm:ss ZZZZ");
};

export const prettyDateTime = (dateStr = "") => {
  return isoToDate(dateStr).toFormat("yyyy-MM-dd HH:mm:ss ZZZZ");
};

export const prettyEnglishDate = (dateStr = "") => {
  return isoToDate(dateStr).toFormat("LLL d, yyyy");
};

export const prettyEnglishDateTime = (dateStr = "") => {
  return isoToDate(dateStr).toFormat("LLL d, yyyy 'at' HH:mm");
};

export type TzMode = "utc" | "local";

export const prettyEnglishDateTimeTz = (dateStr = "", tz: TzMode = "utc") => {
  const dt = DateTime.fromISO(dateStr, { zone: "utc" });
  const converted = tz === "local" ? dt.toLocal() : dt;
  return converted.toFormat("LLL d, yyyy 'at' HH:mm");
};

export const prettyEnglishDateTimeTzFull = (
  dateStr = "",
  tz: TzMode = "utc",
) => {
  const dt = DateTime.fromISO(dateStr, { zone: "utc" });
  const converted = tz === "local" ? dt.toLocal() : dt;
  return converted.toFormat("LLL d, yyyy 'at' HH:mm ZZZZ");
};

export const formatLatencyMs = (ms: number): string =>
  ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;

export const formatDurationSeconds = (seconds: number): string => {
  const dur = Duration.fromObject({ seconds: seconds }).shiftTo(
    "hours",
    "minutes",
    "seconds",
  );
  const parts: string[] = [];
  if (dur.hours > 0) parts.push(`${dur.hours}h`);
  if (dur.minutes > 0) parts.push(`${dur.minutes}m`);
  if (parts.length === 0) {
    // Show seconds in higher precision if there are no other parts
    parts.push(`${dur.seconds.toFixed(1)}s`);
  } else {
    parts.push(`${dur.seconds.toFixed(0)}s`);
  }
  return parts.join(" ");
};

export const fileDate = (dateStr = "") => {
  return isoToDate(dateStr).toFormat("yyyy-MM-dd");
};

export const fileDateTime = (dateStr = "") => {
  return isoToDate(dateStr).toFormat("yyyy-MM-dd-HH-mm-ss");
};

export const timeBetween = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): string => {
  const start = isoToDate(startDate);
  const end = isoToDate(endDate);
  return end.toRelative({ base: start }) || "";
};

export const dateFromToday = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
};

export const secondsFromNow = (seconds: number) => {
  const d = new Date();
  return new Date(d.getTime() + seconds * 1000);
};

export const isBefore = (a: Date, b: Date) => a < b;

export function findLatestDate<M extends { createdAt: string }>(a: M, b: M) {
  const aDate = new Date(a.createdAt).getTime();
  const bDate = new Date(b.createdAt).getTime();
  return aDate > bDate ? a : b;
}
